import { EmployerKeysToManage } from '@/people/state/types/employer';
import { SurveyTemplate } from '@/surveys/state';

const importedProfileFieldKeys: { [key in keyof EmployerKeysToManage]: string } = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  birthDate: 'Date of Birth',
  role: 'Job Role',
  department: 'Department',
  fiscalCode: 'Tax Code',
  jobLevel: 'Job Level',
  emailManager: 'Manager Email',
  area: 'Area (team)',
};

export const en = {
  configuration: {
    macroStepTitles: {
      addDelegate: 'Add Delegate',
      customize: 'Customize',
      companyData: 'Company Data',
    },
    hr: {
      start: {
        title: 'Hi {{name}}, welcome to Slash',
        subtitle:
          'Set up the platform in just a few simple steps to start taking full advantage of its features. If you skip, it will not be shown again.',
        startConfiguration: 'Let’s Start',
        skipConfiguration: 'Skip Setup',
        verticalStepTitle: 'STEP',
        setup: {
          step1: {
            title: 'Add Delegate',
            description: 'Add a delegate with the same permissions for shared management',
          },
          step2: {
            title: 'Customize',
            description: 'Customize the Slash platform by uploading your company website or logo',
          },
          step3: {
            title: 'Upload Company Data',
            description:
              'Upload the employees’ records, providing first name, last name, and email for each person. Don’t you have a similar file? {{downloadFile}}',
            downloadFileLabel: 'Download the template',
          },
        },
      },
      delegate: {
        add: {
          title: 'Add Delegate',
          subtitle:
            'Assign one or more delegates with your same permissions for shared management. Enter the data correctly and click Add. Delegates will receive an invitation instantly. You can always add more from the Organization section.',
          addDelegate: {
            firstName: 'Delegate First Name',
            lastName: 'Delegate Last Name',
            email: 'Delegate Email',
            cta: {
              skip: 'Skip this step',
              next: 'Add',
              add: 'Add',
            },
          },
        },
        confirm: {
          title: 'Add Delegate',
          subtitle:
            'Assign one or more delegates with your same permissions for shared management. Enter the data correctly and click Add. Delegates will receive an invitation instantly. You can always add more from the Organization section.',
          addDelegate: {
            firstName: 'Delegate First Name',
            lastName: 'Delegate Last Name',
            email: 'Delegate Email',
            firstNamePlaceholder: 'e.g., Mario',
            lastNamePlaceholder: 'e.g., Rossi',
            emailPlaceholder: 'e.g., mariorossi@domain.com',
            cta: {
              skip: 'Yes, add',
              next: 'No, continue',
              add: 'Add',
            },
            alertInvalidEmail: 'The email format is invalid. Please check and try again.',
            infoBox: 'For more information, check the !{DOCS}! or !{CONTACT}!',
            labelDocs: 'guide',
            labelContact: 'contact us',
          },
          currentDelegates: {
            title: 'Delegate',
            firstNameAndLastName: 'Full Name',
            email: 'Email',
            deleted: 'Delegate successfully removed',
          },
        },
      },
      setLogo: {
        title: 'Customize the platform with your logo',
        subtitle:
          'Upload your company logo to make Slash truly yours. Enter your company website or upload the logos manually.',
        siteLabel: 'Your company website',
        sitePlaceholder: 'https://',
        siteDescription:
          '{{remainingDesignQuota}}/{{totalDesignQuota}} attempts available to upload the logo from the provided website',
        alert:
          'For the best experience, we recommend uploading both light and dark versions of your logo.\nIf you upload only one, the missing version will be generated by AI. You can always edit it in the <i>Settings</i> section of the platform.',
        remainingDesignQuotaZeroAlert:
          'You have used all available attempts to upload the logo from the provided website. You can still upload logos manually and choose your theme color.',
        alertError:
          'Unable to retrieve logos from the provided URL. Try a different link or upload them manually.',
        cta: {
          generate: 'Generate',
        },
        loading: {
          title: 'AI is analyzing the provided page',
          description: 'The logos will soon appear in the preview below.',
        },
        uploadLogo: {
          titleWhiteMode: 'Company logo White mode',
          titleTooltipWhiteMode: 'Dark version of the logo, ideal for light backgrounds.',
          titleDarkMode: 'Company logo Dark mode',
          titleTooltipDarkMode: 'Light version of the logo, ideal for dark or colored backgrounds.',
        },
      },
      setColor: {
        title: 'Choose the platform color',
        subtitle:
          'Customize the platform with one of the available colors. You can change this later in Settings.',
        titleColor: 'Theme color',
        previewLabel: 'Platform preview',
        previewLabelTooltip:
          'Here you can preview the platform in dark and light mode. The theme choice is personal and can be managed by each user directly from the dropdown at the top right.',
      },
      setData: {
        title: 'Import company data',
        subtitle:
          'Upload your employees’ data to create their profiles. Make sure each profile includes the following fields: First Name, Last Name, Email.',
        warning:
          'You can also add the following optional data to make the analyses more accurate: Date of birth (dd-mm-yyyy), Job title, Department, Manager (first name, last name, email), Team, Tax code, Job level.',
        downloadLabel: 'Download our template',
        downloadFile:
          'Don’t have a similar file?\n {{downloadFile}} and fill it in with your employees’ data.',
        uploadedFiles: 'Uploaded document',
        loadingFile: {
          title: 'AI is verifying the uploaded data',
          description: 'The employee records will soon appear on the platform.',
        },
        infoBox: 'For information or support with the upload, check the !{DOCS}! or !{CONTACT}!',
        labelDocs: 'guide',
        labelContact: 'contact us',
        warningSaveBeforeQuit:
          '<b>Warning</b> If you leave without completing the review and correction of the employee records, the uploaded file will not be saved. Make sure to complete the operation before leaving.',
      },
      verifyData: {
        title: 'Verify Employee Data',
        subtitle:
          'Here’s a summary of the uploaded employee records. Fix any missing data so everyone can receive the assessment and access the platform. If you can’t correct them now, confirm and continue — you can fix them later in the Employee Records section.',
        charts: {
          totalProfiles: 'Total Records',
          completeProfiles: 'Complete Profiles',
          incompleteProfiles: 'Incomplete Profiles',
          profilesLabel: 'Profiles',
        },
        previewDataWarning: {
          title: 'Data Verification',
          incompleteData: {
            message: {
              singular: 'Missing or incomplete data in 1 profile',
              plural: 'Missing or incomplete data in {{count}} profiles',
            },
            fieldKeys: {
              ...importedProfileFieldKeys,
            },
          },
        },
        dialogConfirmNavigateBack: {
          title: 'Unconfirmed Records',
          description: 'Going back will discard the entered information.',
          explicitQuestion: 'Are you sure you want to go back?',
          cancel: 'Cancel',
          confirm: 'Yes, go back',
        },
        dialogEditImportedProfiles: {
          title: 'Employee Records List',
          description:
            'Here you’ll find the list of employee records with issues. You can edit or complete them directly from this screen, or later from the Employee Records section of the platform. All changes are saved automatically in real time — even if you close the window, your updates will remain saved.',
          cta: {
            confirm: 'Confirm records',
            showData: 'View profiles',
          },
          table: {
            name: 'First Name',
            surname: 'Last Name',
            email: 'Email',
            birthDate: 'Date of Birth',
            role: 'Job Role',
            department: 'Department',
            manager: 'Manager email',
            area: 'Area (team)',
            fiscalCode: 'Tax Code',
            jobLevel: 'Job Level',
            filters: {
              title: 'Filters',
              cta: {
                close: 'Close',
              },
            },
          },
          filters: {
            importedProfileCompleted: 'Complete Profiles',
            importedProfileIncompleted: 'Incomplete Profiles',
            importedProfileAll: 'All Profiles',
            cta: {
              submit: 'Apply',
              reset: 'Reset',
            },
          },
        },
      },
      typ: {
        configurationCompletedThankYouPage: {
          title: 'Configuration Completed!',
          subtitle:
            'The platform is ready! Start engaging your employees and unlocking their potential.',
          cta: {
            next: 'Go to Slash',
          },
        },
      },
    },
    learning: {
      start: {
        title: 'Hi {{name}}, welcome to Slash',
        subtitle: 'Confirm your data and complete the assessment to start your journey!',
        startConfiguration: 'Let’s Start',
      },
      employeeConfirmData: {
        title: 'Check your personal data',
        subtitle:
          'Verify that your personal data is correct. If you notice errors, contact your HR representative.',
        disclaimer: 'Click Next to confirm your data.',
        profile: {
          role: 'Role',
          department: 'Department',
          manager: 'Manager',
          email: 'Email',
          birthDate: 'Date of Birth',
        },
      },
      employeeStartSurvey: {
        previousButton: 'Back to Previous Section',
        title: 'Complete the assessment',
        subtitle:
          'Answer a few simple questions to help us outline your profile and identify your {{templateName}}.',
        disclaimer: {
          [SurveyTemplate.SoftSkillAssessment]:
            'It will take approximately 15 minutes to complete. Once started, it cannot be paused: if you exit early, data will not be saved. When answering, think about how you would behave in everyday situations, not necessarily related to the professional field.',
          [SurveyTemplate.HardSkillAssessment]:
            'It will take approximately 15 minutes to complete. Once started, it cannot be paused: if you exit early, data will not be saved. When answering, refer to your current knowledge and how you would interpret or address the proposed situations.',
        },
      },
      employeeCompleteSurvey: {
        title: 'Complete the assessment',
        subtitle: 'Let’s start with you — discover your profile.',
        stepXonY: '{{currentStep}} of {{totalSteps}} questions',
        exit: 'Exit assessment',
        confirmExitFromSurveyDialog: {
          title: 'Exit assessment',
          description:
            'By exiting the assessment, you will be able to complete it later by starting over. Are you sure you want to exit?',
          confirm: 'Exit',
          cancel: 'Cancel',
        },
      },
      typ: {
        configurationCompletedThankYouPage: {
          title: 'You have successfully completed the assessment!',
          subtitle:
            'Responses have been submitted successfully.\nReturn to the homepage to view available assessments and continue exploring the platform.',
          cta: {
            next: 'Go to Slash',
          },
        },
      },
    },
  },
};
